// API configuration and utilities
// Use environment variables for dynamic configuration
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';
// export const API_BASE_URL = 'https://apidev29logis.us-fx.pro/api/v1';  // production

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

export interface LoginRequest {
  email: string; // This field can now accept either email or username
  password: string;
  'g-recaptcha-response': string;
}

export interface ForgotPasswordRequest {
  email: string;
  'g-recaptcha-response': string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  password_confirmation: string;
}

export interface LoginResponse {
  user: {
    google_auth_enabled: any;
    id: number;
    email: string;
    name?: string;
    role?: string;
  };
  token?: string;
  access_token?: string;
}

export interface UserResponse {
  id: number;
  email: string;
  name?: string;
  role?: string;
  permissions?: string[];
  // Add other user fields as needed
}

export interface PermissionsResponse {
  permissions: string[];
}

// Generic API request function
export async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: data.message || `HTTP ${response.status}: ${response.statusText}`,
        errors: data.errors,
      };
    }

    return {
      success: true,
      data: data.data || data, // Handle both nested and direct data structures
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Network error occurred',
    };
  }
}

// Auth-specific API functions
export const authApi = {
  login: async (credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> => {
    return apiRequest<LoginResponse>('/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  logout: async (token: string): Promise<ApiResponse> => {
    return apiRequest('/logout', {
      method: 'GET',
      ...withAuth(token),
    });
  },

  getUser: async (token: string): Promise<ApiResponse<UserResponse>> => {
    return apiRequest<UserResponse>('/user', {
      method: 'GET',
      ...withAuth(token),
    });
  },

  getPermissions: async (token: string): Promise<ApiResponse<PermissionsResponse>> => {
    return apiRequest<PermissionsResponse>('/auth/permissions', {
      method: 'GET',
      ...withAuth(token),
    });
  },

  forgotPassword: async (request: ForgotPasswordRequest): Promise<ApiResponse> => {
    return apiRequest('/forgot-password', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  resetPassword: async (request: ResetPasswordRequest): Promise<ApiResponse> => {
    return apiRequest('/reset-password', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  verifyResetToken: async (token: string): Promise<ApiResponse> => {
    return apiRequest(`/reset-password/${token}`, {
      method: 'GET',
    });
  },

  // Two-Factor Authentication methods
  getTwoFactorQrCode: async (authToken: string): Promise<ApiResponse> => {
    return apiRequest('/2fa/qr-code', {
      method: 'GET',
      ...withAuth(authToken),
    });
  },

  enableTwoFactor: async (authToken: string, data: { secret_key: string; verification_code: string }): Promise<ApiResponse> => {
    return apiRequest('/2fa/enable', {
      method: 'POST',
      body: JSON.stringify(data),
      ...withAuth(authToken),
    });
  },

  disableTwoFactor: async (authToken: string): Promise<ApiResponse> => {
    return apiRequest('/2fa/disable', {
      method: 'POST',
      ...withAuth(authToken),
    });
  },

  getTwoFactorStatus: async (authToken: string): Promise<ApiResponse> => {
    return apiRequest('/2fa/status', {
      method: 'GET',
      ...withAuth(authToken),
    });
  },

  verifyTwoFactorCode: async (authToken: string, code: string): Promise<ApiResponse> => {
    return apiRequest('/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
      ...withAuth(authToken),
    });
  },
};

// Store-specific API functions
export const storeApi = {
  getUserStores: async (token: string): Promise<ApiResponse> => {
    return apiRequest('/stores', {
      method: 'GET',
      ...withAuth(token),
    });
  },

  validateStoreAccess: async (token: string, storeId: number): Promise<ApiResponse> => {
    return apiRequest('/stores/validate', {
      method: 'POST',
      body: JSON.stringify({ store_id: storeId }),
      ...withAuth(token),
    });
  },
};

// Marketplace Orders API functions
export const marketplaceOrdersApi = {
  getOrders: async (
    token: string, 
    storeId: number, 
    type: string, 
    page: number = 1, 
    limit: number = 10
  ): Promise<ApiResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      type: type,
      store_id: storeId.toString()
    });

    return apiRequest(`/marketplace-orders?${params}`, {
      method: 'GET',
      ...withAuth(token),
    });
  },

  filterOrders: async (
    token: string,
    filters: {
      type?: string;
      search?: string;
      marketplace_filter?: string[];
      date_from?: string;
      date_to?: string;
      page?: number;
      limit?: number;
      store_id?: number;
    }
  ): Promise<ApiResponse> => {
    // Use direct fetch to preserve the full Laravel paginator structure (data + meta)
    const url = `${API_BASE_URL}/marketplace-orders/filter`;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(filters),
      });
      const json = await res.json();
      if (!res.ok) {
        return { success: false, message: json.message || `HTTP ${res.status}: ${res.statusText}` };
      }
      return { success: !!json.success, data: json.data, message: json.message };
    } catch (err) {
      return { success: false, message: err instanceof Error ? err.message : 'Network error occurred' };
    }
  },
};

// Notifications API
export type NotificationTab = 'all' | 'unread' | 'read' | 'mentioned';

export const notificationsApi = {
  getNotifications: async (
    token: string,
    params: { tab?: NotificationTab; page?: number; limit?: number } = {}
  ): Promise<ApiResponse> => {
    const search = new URLSearchParams();
    if (params.tab) search.set('tab', params.tab);
    if (params.page) search.set('page', String(params.page));
    if (params.limit) search.set('limit', String(params.limit));

    return apiRequest(`/notifications?${search.toString()}`.replace(/\?$/, ''), {
      method: 'GET',
      ...withAuth(token),
    });
  },
};

// Locations API (Cities/Areas)
export const locationApi = {
  getCities: async (token: string, storeId: number): Promise<ApiResponse<Array<{ id: number; name: string }>>> => {
    const params = new URLSearchParams({ store_id: String(storeId) });
    return apiRequest<Array<{ id: number; name: string }>>(`/cities?${params.toString()}`, {
      method: 'GET',
      ...withAuth(token),
    });
  },

  getAreas: async (token: string, storeId: number, city: string): Promise<ApiResponse<Array<{id: string; name: string; zone: string | null; city: string}>>> => {
    const params = new URLSearchParams({ store_id: String(storeId), city });
    return apiRequest(`/areas?${params.toString()}`, {
      method: 'GET',
      ...withAuth(token),
    });
  },
};

// Help Desk API
export type HelpDeskTab = 'all-tickets' | 'open' | 'closed' | 'solved' | 'inprogress';

export const helpDeskApi = {
  getTickets: async (
    token: string,
    params: { tab?: HelpDeskTab; page?: number; limit?: number } = {}
  ): Promise<ApiResponse> => {
    const search = new URLSearchParams();
    if (params.tab) search.set('tab', params.tab);
    if (params.page) search.set('page', String(params.page));
    if (params.limit) search.set('limit', String(params.limit));

    return apiRequest(`/helpdesk?${search.toString()}`.replace(/\?$/, ''), {
      method: 'GET',
      ...withAuth(token),
    });
  },
};

// Dashboard-specific API functions
export const dashboardApi = {
  getOverview: async (token: string, storeId: number, filters: { include_test_order?: boolean; marketplace_filter?: string[] } = {}): Promise<ApiResponse> => {
    const params = new URLSearchParams();
    params.append('store_id', storeId.toString());
    
    if (filters.include_test_order !== undefined) {
      params.append('include_test_order', filters.include_test_order ? 'true' : 'false');
    }
    if (filters.marketplace_filter && filters.marketplace_filter.length > 0) {
      filters.marketplace_filter.forEach(marketplace => {
        params.append('marketplace_filter[]', marketplace);
      });
    }

    // Use statistics endpoint for detailed status counts (matching legacy dashboard)
    return apiRequest(`/dashboard/statistics?${params}`, {
      method: 'GET',
      ...withAuth(token, storeId),
    });
  },

  getStatistics: async (token: string, storeId: number, includeTestOrder: boolean = false): Promise<ApiResponse> => {
    const params = new URLSearchParams({
      store_id: storeId.toString(),
      include_test_order: includeTestOrder ? 'true' : 'false',
    });

    return apiRequest(`/dashboard/statistics?${params}`, {
      method: 'GET',
      ...withAuth(token, storeId),
    });
  },

  getMarketplaces: async (token: string): Promise<ApiResponse> => {
    return apiRequest('/dashboard/marketplaces', {
      method: 'GET',
      ...withAuth(token),
    });
  },
};

// Two-Factor Authentication API functions
export const twoFactorApi = {
  getQRCode: async (token: string): Promise<ApiResponse> => {
    return apiRequest('/2fa/qr-code', {
      method: 'GET',
      ...withAuth(token),
    });
  },

  verifyCode: async (token: string, code: string): Promise<ApiResponse> => {
    return apiRequest('/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
      ...withAuth(token),
    });
  },

  enable: async (token: string, code: string): Promise<ApiResponse> => {
    return apiRequest('/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ code }),
      ...withAuth(token),
    });
  },

  disable: async (token: string): Promise<ApiResponse> => {
    return apiRequest('/2fa/disable', {
      method: 'POST',
      ...withAuth(token),
    });
  },

  getStatus: async (token: string): Promise<ApiResponse> => {
    return apiRequest('/2fa/status', {
      method: 'GET',
      ...withAuth(token),
    });
  },
};

// Shipment Item Confirmation API functions
export const shipmentItemApi = {
  arrivedOrder: async (
    token: string, 
    data: {
      order_product_id: number[] | number;
      master_way_bill: string;
      order_confirmed: string;
      store_id?: number;
    }
  ): Promise<ApiResponse> => {
    return apiRequest('/shipment-items/arrived', {
      method: 'POST',
      body: JSON.stringify(data),
      ...withAuth(token),
    });
  },

  declineOrder: async (
    token: string, 
    data: {
      order_product_id: number[] | number;
      master_way_bill: string;
      order_confirmed: string;
      store_id?: number;
    }
  ): Promise<ApiResponse> => {
    return apiRequest('/shipment-items/decline', {
      method: 'POST',
      body: JSON.stringify(data),
      ...withAuth(token),
    });
  },

  foundOrder: async (
    token: string, 
    data: {
      order_product_id: number[] | number;
      master_way_bill: string;
      order_confirmed: string;
      store_id?: number;
      item_order_id?: string;
    }
  ): Promise<ApiResponse> => {
    return apiRequest('/shipment-items/found', {
      method: 'POST',
      body: JSON.stringify(data),
      ...withAuth(token),
    });
  },
};

// Tracking API functions
export const trackingApi = {
  submitToFacility: async (token: string, orderItemIds: number[], storeId: number): Promise<ApiResponse> => {
    return apiRequest('/tracking/submit-to-facility', {
      method: 'POST',
      body: JSON.stringify({
        orderItemIds,
        store_id: storeId
      }),
      ...withAuth(token),
    });
  },
  shipToDestination: async (token: string, orderItemIds: number[], storeId: number): Promise<ApiResponse> => {
    return apiRequest('/tracking/ship-to-destination', {
      method: 'POST',
      body: JSON.stringify({
        orderItemIds,
        store_id: storeId
      }),
      ...withAuth(token),
    });
  },
  // Full shipment flow for moving orders to Outbound
  createShipmentFlow: async (token: string, orderItemIds: number[], storeId: number): Promise<ApiResponse> => {
    try {
      // Step 1: Generate Master Waybill
      const waybillResponse = await shipmentApi.generateMasterWayBill(token, storeId);
      if (!waybillResponse.success) {
        return waybillResponse;
      }
      
      const masterWayBill = waybillResponse.data?.masterWayBill;
      if (!masterWayBill) {
        return {
          success: false,
          message: 'Failed to generate master waybill'
        };
      }

      // Step 2: Update Master Waybill with order products
      const updateResponse = await shipmentApi.updateMasterWayBill(token, storeId, orderItemIds, masterWayBill);
      if (!updateResponse.success) {
        return updateResponse;
      }

      // Step 3: Get Items Count (validation)
      const countResponse = await shipmentApi.getItemsCount(token, storeId, masterWayBill);
      if (!countResponse.success) {
        return countResponse;
      }

      // Step 4: Create Shipment
      const shipmentResponse = await shipmentApi.createShipment(token, {
        store_id: storeId,
        orderProductIds: orderItemIds,
        masterWayBill: masterWayBill
      });

      return shipmentResponse;
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to complete shipment flow'
      };
    }
  },
};

// Shipment API functions
export const shipmentApi = {
  // Get shipments with pagination and filtering
  getShipments: async (token: string, storeId: number, params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }): Promise<ApiResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.append('store_id', String(storeId));
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);

    return apiRequest(`/shipments?${queryParams}`, {
      method: 'GET',
      ...withAuth(token),
    });
  },

  // Create shipment
  createShipment: async (token: string, data: {
    store_id: number;
    // Backend now supports deriving items by master waybill; IDs are optional
    orderProductIds: number[];
    masterWayBill?: string;
    masterWayBills?: string[];
    shipment_reference?: string;
    tracking_no?: string;
    items?: Array<{
      product_name: string;
      quantity: number;
      weight?: number;
      dimensions?: string;
    }>;
  }): Promise<ApiResponse> => {
    console.log('data createShipment slice', data);
    return apiRequest('/shipments', {
      method: 'POST',
      body: JSON.stringify(data),
      ...withAuth(token),
    });
  },

  // Get shipment by ID
  getShipment: async (token: string, id: number): Promise<ApiResponse> => {
    return apiRequest(`/shipments/${id}`, {
      method: 'GET',
      ...withAuth(token),
    });
  },

  // Update shipment
  updateShipment: async (token: string, id: number, data: any): Promise<ApiResponse> => {
    return apiRequest(`/shipments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...withAuth(token),
    });
  },

  // Delete shipment
  deleteShipment: async (token: string, id: number): Promise<ApiResponse> => {
    return apiRequest(`/shipments/${id}`, {
      method: 'DELETE',
      ...withAuth(token),
    });
  },

  // Get items count by master waybill
  getItemsCount: async (token: string, storeId: number, masterWayBill?: string, shipmentId?: number): Promise<ApiResponse> => {
    const params = new URLSearchParams();
    params.append('store_id', storeId.toString());
    if (masterWayBill) params.append('masterWayBill', masterWayBill);
    
    const endpoint = shipmentId ? `/shipments/${shipmentId}/items-count` : '/shipments/items-count';
    return apiRequest(`${endpoint}?${params}`, {
      method: 'GET',
      ...withAuth(token),
    });
  },

  // Generate master way bill
  generateMasterWayBill: async (token: string, storeId: number): Promise<ApiResponse> => {
    return apiRequest('/shipments/generate-master-way-bill', {
      method: 'POST',
      body: JSON.stringify({ store_id: storeId }),
      ...withAuth(token),
    });
  },

  // Update master way bill
  updateMasterWayBill: async (token: string, storeId: number, orderProductIds: number[], masterWayBill: string): Promise<ApiResponse> => {
    console.log('orderProductIds updateMasterWayBill slice', orderProductIds);
    console.log('masterWayBill updateMasterWayBill slice', masterWayBill);
    return apiRequest('/shipments/update-master-way-bill', {
      method: 'POST',
      body: JSON.stringify({
        store_id: storeId,
        orderProductIds,
        masterWayBill
      }),
      ...withAuth(token),
    });
  },

  // Get items for a shipment
  getShipmentItems: async (
    token: string,
    storeId: number,
    shipmentId: number,
    filters?: {
      shipmentReference?: string;
      masterWayBill?: string;
    }
  ): Promise<ApiResponse> => {
    const params = new URLSearchParams({ store_id: String(storeId) });
    if (filters?.shipmentReference) {
      params.set('shipment_reference', filters.shipmentReference);
    }
    if (filters?.masterWayBill) {
      params.set('master_way_bill', filters.masterWayBill);
    }
    return apiRequest(`/shipments/${shipmentId}/items?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'X-Store-ID': String(storeId),
      },
    });
  },
};
// Distribution (Destination Warehouse) API
export const distributionApi = {
  generateBatchId: async (token: string, storeId: number): Promise<ApiResponse<{ batchId: string }>> => {
    return apiRequest('/tracking/generate-batch-id', {
      method: 'POST',
      body: JSON.stringify({ store_id: storeId }),
      ...withAuth(token),
    });
  },

  updateBatchId: async (token: string, storeId: number, ids: number[], batchId: string): Promise<ApiResponse> => {
    return apiRequest('/tracking/update-batch-id', {
      method: 'POST',
      body: JSON.stringify({ store_id: storeId, orderItemIds: ids, batchId }),
      ...withAuth(token),
    });
  },

  updateDistributionCenter: async (token: string, ids: number[], distributionCenterId: number): Promise<ApiResponse> => {
    return apiRequest('/tracking/update-distribution-center', {
      method: 'POST',
      body: JSON.stringify({ orderItemIds: ids, distributionCenterId }),
      ...withAuth(token),
    });
  },

  distributionAction: async (token: string, storeId: number, ids: number[], distributionCenterId: number): Promise<ApiResponse> => {
    return apiRequest('/tracking/distribution-action', {
      method: 'POST',
      body: JSON.stringify({ store_id: storeId, orderItemIds: ids, distributionCenterId }),
      ...withAuth(token),
    });
  },
};

// Helper to add auth token to requests
export function withAuth(token: string, storeId?: number) {
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
  };
  
  // Add store ID header if provided
  if (storeId !== undefined) {
    headers['X-Store-ID'] = storeId.toString();
  }
  
  return {
    headers,
  };
}
